import React from "react";
import * as XLSX from "xlsx-js-style";
import { IBranch } from "../../models/Branch";
import EmptyImage from "../../components/EmptyImage.tsx";
import PayrollBranch from "./PayrollBranch.tsx";
import { PAYROLL_CUADRO_COLUMN_LABELS } from "./PayrollHeader.tsx";
import { toTitleCase } from "./PartnerCell.tsx";
import type { CuadroMandoTableRowSnapshot } from "./RowPayRoll.tsx";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import BulkRecibosModal from "../../components/nomina/BulkRecibosModal.tsx";
import { notification } from "../../utils/Notifications.tsx";
import { getCurrencyFormat } from "../../utils/index.ts";
import {
  findPaymentDetail,
  findChartDaysWorked,
  isPayrollFetchAborted,
} from "../../services/payroll.service.ts";
import {
  computePartnerPayBreakdownFromDetail,
  getCuadroDeductionColumnsForExport,
} from "../../utils/payrollUtils.ts";
import { mergeDailyPaymentsByDate } from "../../utils/payrollVisitHours.ts";
import { useFullDayHours } from "../../hooks/useFullDayHours.tsx";
import PaymentEstimadoTotal from "./PaymentEstimadoTotal.tsx";

// Hook que calcula el total igual que PaymentDetail.tsx
const usePartnerTotalCalculation = (
  record: any,
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs },
  branch: number | undefined,
  fullDayHours: number
) => {
  const [fetchedData, setFetchedData] = React.useState<{
    dailyPayments?: any[];
    freeDaysEnjoyedDates?: string[];
  } | null>(null);
  const [chartDaysData, setChartDaysData] = React.useState<{
    freeDaysDates?: string[];
    savedIncidents?: any[];
  }>({
    freeDaysDates: [],
    savedIncidents: [],
  });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!record.partnerId || !branch || !dates) {
      setFetchedData(null);
      setChartDaysData({ freeDaysDates: [] });
      return;
    }

    const ac = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      try {
        const startDate = dates.start.format("YYYY-MM-DD");
        const endDate = dates.end.format("YYYY-MM-DD");

        const [paymentResponse, chartResponse] = await Promise.all([
          findPaymentDetail(
            record.partnerId,
            branch,
            startDate,
            endDate,
            ac.signal
          ),
          findChartDaysWorked(
            record.partnerId,
            branch,
            startDate,
            endDate,
            ac.signal
          ),
        ]);

        if (ac.signal.aborted) return;

        setFetchedData({
          dailyPayments: mergeDailyPaymentsByDate(
            paymentResponse.dailyPayments || []
          ),
          freeDaysEnjoyedDates: paymentResponse.freeDaysEnjoyedDates || [],
        });
        setChartDaysData({
          freeDaysDates: chartResponse.freeDaysDates || [],
          savedIncidents: chartResponse.savedIncidents || [],
        });
      } catch (error) {
        if (isPayrollFetchAborted(error)) return;
        console.error("Error fetching payment detail:", error);
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => ac.abort();
  }, [record.partnerId, branch, dates?.start?.format("YYYY-MM-DD"), dates?.end?.format("YYYY-MM-DD")]);

  const partnerPayBreakdown = React.useMemo(() => {
    if (!fetchedData?.dailyPayments?.length) {
      return null;
    }
    return computePartnerPayBreakdownFromDetail({
      paymentDetail: fetchedData,
      chartFreeDaysDates: chartDaysData?.freeDaysDates,
      savedIncidents: chartDaysData?.savedIncidents,
      record,
      fullDayHours,
    });
  }, [fetchedData, chartDaysData, record, fullDayHours]);

  if (loading) {
    return {
      totalDailyPayments: 0,
      totalIncentives: 0,
      totalDeductions: 0,
      totalMissingBoxDeductions: 0,
      uniformDeduction: 0,
      totalPayWithIncentives: 0,
      totalPayWithIncentivesFormatted: getCurrencyFormat(0),
    };
  }

  if (!partnerPayBreakdown) {
    return {
      totalDailyPayments: 0,
      totalIncentives: 0,
      totalDeductions: 0,
      totalMissingBoxDeductions: 0,
      uniformDeduction: 0,
      totalPayWithIncentives: 0,
      totalPayWithIncentivesFormatted: getCurrencyFormat(0),
    };
  }

  return {
    ...partnerPayBreakdown,
    totalPayWithIncentivesFormatted: getCurrencyFormat(
      partnerPayBreakdown.totalPayWithIncentives
    ),
  };
};

// Componente que renderiza el desglose de un colaborador usando el hook
const PartnerCalculationDisplay = ({
  record,
  dates,
  branch,
  fullDayHours,
  index,
  onCalculationUpdate,
}: {
  record: any;
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  branch: number | undefined;
  fullDayHours: number;
  index: number;
  onCalculationUpdate?: (partnerId: number, calculation: any) => void;
}) => {
  const partnerCalculation = usePartnerTotalCalculation(
    record,
    dates,
    branch,
    fullDayHours
  );

  // Actualizar el estado del padre cuando cambian los valores calculados
  React.useEffect(() => {
    if (partnerCalculation && onCalculationUpdate && record.partnerId) {
      onCalculationUpdate(record.partnerId, partnerCalculation);
    }
  }, [partnerCalculation, record.partnerId, onCalculationUpdate]);

  const dailyPayments =
    partnerCalculation?.totalDailyPayments ||
    record.paymentDetails?.totalPayNumber ||
    0;

  const totalIncentives =
    partnerCalculation?.totalIncentives ||
    Number((record.totalIncentives || "$0.00").replace(/[^0-9.-]+/g, "") || 0);

  const parseCuadroMoney = (v: unknown) =>
    Number(String(v ?? "").replace(/[^0-9.-]+/g, "")) || 0;

  const totalDeductions = (() => {
    if (partnerCalculation?.totalDeductions != null) {
      return partnerCalculation.totalDeductions;
    }
    const tarjetas = parseCuadroMoney(record.tarjetasDeductions);
    const excluding = parseCuadroMoney(
      record.totalDeductionsExcludingTarjetas ?? record.totalDeductions
    );
    if (tarjetas > 0 || record.totalDeductionsExcludingTarjetas != null) {
      return excluding + tarjetas;
    }
    return parseCuadroMoney(record.totalDeductions);
  })();

  const missingBoxDeductions =
    partnerCalculation?.totalMissingBoxDeductions ||
    (record.calculatedMissingBoxes || []).reduce(
      (sum: number, missingBox: any) => sum + (missingBox.calculatedAmount || 0),
      0
    );

  const uniformDeduction =
    partnerCalculation?.uniformDeduction ||
    Number((record.uniformDeduction || "0").replace(/[^0-9.-]+/g, "") || 0);

  const totalPayWithIncentives =
    partnerCalculation?.totalPayWithIncentives ||
    dailyPayments +
      totalIncentives -
      (totalDeductions + missingBoxDeductions + uniformDeduction);

  return (
    <div
      key={record.partnerId || index}
      style={{
        marginBottom: "12px",
        padding: "12px",
        background: "#ffffff",
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          paddingBottom: "8px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <span
          style={{
            color: "#1e293b",
            fontWeight: 700,
            fontSize: "0.85rem",
          }}
        >
          {record.name || "Sin nombre"}
        </span>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span
            style={{
              color: "#1F4E79",
              fontWeight: 700,
              fontSize: "0.9rem",
            }}
          >
            {getCurrencyFormat(totalPayWithIncentives)}
          </span>
          <span
            style={{
              color: "#64748b",
              fontWeight: 500,
              fontSize: "0.65rem",
              fontFamily: "monospace",
              marginTop: "2px",
            }}
          >
            Backend: {Number((record.totalPayWithIncentives || "$0.00").replace(/[^0-9.-]+/g, "") || 0).toFixed(2)}
          </span>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          fontSize: "0.7rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "4px 8px",
            background: "#f8fafc",
            borderRadius: "6px",
          }}
        >
          <span style={{ color: "#64748b", fontWeight: 500 }}>
            Pagos Diarios:
          </span>
          <span style={{ color: "#1e293b", fontWeight: 600, fontFamily: "monospace" }}>
            {dailyPayments.toFixed(2)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "4px 8px",
            background: "#f0fdf4",
            borderRadius: "6px",
          }}
        >
          <span style={{ color: "#64748b", fontWeight: 500 }}>
            + Incentivos:
          </span>
          <span style={{ color: "#16a34a", fontWeight: 600, fontFamily: "monospace" }}>
            +{totalIncentives.toFixed(2)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "4px 8px",
            background: "#fef2f2",
            borderRadius: "6px",
          }}
        >
          <span style={{ color: "#64748b", fontWeight: 500 }}>
            - Deducciones:
          </span>
          <span style={{ color: "#dc2626", fontWeight: 600, fontFamily: "monospace" }}>
            -{totalDeductions.toFixed(2)}
          </span>
        </div>
        {missingBoxDeductions > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 8px",
              background: "#fef2f2",
              borderRadius: "6px",
            }}
          >
            <span style={{ color: "#64748b", fontWeight: 500 }}>
              - Faltantes:
            </span>
            <span style={{ color: "#dc2626", fontWeight: 600, fontFamily: "monospace" }}>
              -{missingBoxDeductions.toFixed(2)}
            </span>
          </div>
        )}
        {uniformDeduction > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 8px",
              background: "#fef2f2",
              borderRadius: "6px",
            }}
          >
            <span style={{ color: "#64748b", fontWeight: 500 }}>
              - Uniforme:
            </span>
            <span style={{ color: "#dc2626", fontWeight: 600, fontFamily: "monospace" }}>
              -{uniformDeduction.toFixed(2)}
            </span>
          </div>
        )}
      </div>
      <div
        style={{
          marginTop: "8px",
          padding: "6px 10px",
          background: "#f1f5f9",
          borderRadius: "6px",
          fontSize: "0.65rem",
          fontFamily: "monospace",
          color: "#475569",
          textAlign: "right",
        }}
      >
        {dailyPayments.toFixed(2)} + {totalIncentives.toFixed(2)} - {totalDeductions.toFixed(2)}
        {missingBoxDeductions > 0 ? ` - ${missingBoxDeductions.toFixed(2)}` : ""}
        {uniformDeduction > 0 ? ` - ${uniformDeduction.toFixed(2)}` : ""}
        {" = "}
        <span style={{ fontWeight: 700, color: "#1F4E79" }}>
          {totalPayWithIncentives.toFixed(2)}
        </span>
      </div>
    </div>
  );
};

export interface SpecialPartner {
  record: any;
  rawRecord: any;
  customDates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
}

const CuadroMando = ({
  selectedBranch,
  dates,
}: {
  selectedBranch: IBranch[];
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
}) => {
  const { selectedPartners, records } = useSelector(
    (state: RootState) => state.payroll
  );
  const [showBulkReciboModal, setShowBulkReciboModal] = React.useState(false);
  const [branchSelected, setBranchSelected] = React.useState<IBranch | null>(
    selectedBranch.length > 0 ? selectedBranch[0] : null
  );
  const [viewMode, setViewMode] = React.useState<"cuadro" | "resumen">("cuadro");
  const [exportResumenExcel, setExportResumenExcel] = React.useState<(() => void) | null>(null);
  const [extendedExportApi, setExtendedExportApi] = React.useState<{
    export: () => void;
    canExport: boolean;
  } | null>(null);
  const { fullDayHours: fullDayHoursFromHook } = useFullDayHours(
    branchSelected ? Number(branchSelected.id) : undefined
  );
  const fullDayHours = React.useMemo(() => {
    const value =
      typeof fullDayHoursFromHook === "number" &&
      !isNaN(fullDayHoursFromHook) &&
      fullDayHoursFromHook > 0
        ? fullDayHoursFromHook
        : 8.5;
    return value;
  }, [fullDayHoursFromHook]);

  // Estado para almacenar los valores calculados de cada colaborador
  const [partnerCalculations, setPartnerCalculations] = React.useState<
    Map<number, {
      totalDailyPayments: number;
      totalIncentives: number;
      totalDeductions: number;
      totalMissingBoxDeductions: number;
      uniformDeduction: number;
      totalPayWithIncentives: number;
    }>
  >(new Map());

  const [cuadroExportSnapshots, setCuadroExportSnapshots] = React.useState<
    Map<number, CuadroMandoTableRowSnapshot>
  >(new Map());

  const handleCuadroExportSnapshot = React.useCallback(
    (partnerId: number, snapshot: CuadroMandoTableRowSnapshot | null) => {
      setCuadroExportSnapshots((prev) => {
        const next = new Map(prev);
        if (snapshot === null) next.delete(partnerId);
        else next.set(partnerId, snapshot);
        return next;
      });
    },
    []
  );

  // Limpiar los cálculos cuando cambian los records o la sucursal
  React.useEffect(() => {
    setPartnerCalculations(new Map());
    setCuadroExportSnapshots(new Map());
    setExtendedExportApi(null);
  }, [records.map(r => r.partnerId).join(','), branchSelected?.id]);

  const [specialPartners, setSpecialPartners] = React.useState<SpecialPartner[]>([]);

  const handleAddSpecialPartner = React.useCallback((sp: SpecialPartner) => {
    setSpecialPartners((prev) => [...prev, sp]);
  }, []);

  const handleRemoveSpecialPartner = React.useCallback((partnerId: number) => {
    setSpecialPartners((prev) => prev.filter((sp) => sp.record.partnerId !== partnerId));
  }, []);

  const safeToFixed = (value: any, decimals: number = 2): string => {
    if (value === null || value === undefined) return "0.00";
    const num = typeof value === "number" ? value : parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(decimals);
  };

  const canExportCuadroTabla =
    records.length > 0 || specialPartners.length > 0;

  const exportTableToExcel = () => {
    const rowsForExport = [
      ...records,
      ...specialPartners.map((sp) => sp.record),
    ];

    if (rowsForExport.length === 0) {
      notification("Aviso", "No hay datos para exportar", "warning");
      return;
    }

    const parseMoney = (v: any) =>
      Number(String(v ?? "").replace(/[^0-9.-]+/g, "")) || 0;

    const extraHoursNumeric = (
      snap: CuadroMandoTableRowSnapshot | undefined,
      record: any
    ) => {
      if (snap?.horasExtras) {
        const m = String(snap.horasExtras).match(/^(\d+)/);
        if (m) return parseInt(m[1], 10);
      }
      return parseFloat(safeToFixed(record.extraHours)) || 0;
    };

    const workbook = XLSX.utils.book_new();
    const startDate = dates.start.format("DD-MM-YYYY");
    const endDate = dates.end.format("DD-MM-YYYY");
    const branchName = branchSelected?.name || "Sin sucursal";

    const data: any[][] = [];

    data.push(["CUADRO DE MANDO - NÓMINA"]);
    data.push([]);
    data.push([
      "SUCURSAL:",
      branchName,
      "",
      "PERIODO:",
      `${startDate} al ${endDate}`,
    ]);
    data.push(["TOTAL COLABORADORES:", rowsForExport.length]);
    data.push([]);

    const headers = [...PAYROLL_CUADRO_COLUMN_LABELS];
    data.push(headers);

    rowsForExport.forEach((record: any) => {
      const snap = cuadroExportSnapshots.get(record.partnerId);
      const deductions = getCuadroDeductionColumnsForExport(record);
      const chilitoFallback = `${toTitleCase(record.name || "")}${
        record.position ? `\n${record.position}` : ""
      }`;
      const row = [
        snap?.chilitoText ?? chilitoFallback,
        record.salaryPerFortnight || "$0.00",
        record.dailySalary || "$0.00",
        record.payPerHour || "$0.00",
        snap?.daysWorked ?? record.daysWorked ?? 0,
        snap?.jornadasCompletadas ?? record.jornadasCompletas ?? 0,
        snap?.horasTrabajadas ?? safeToFixed(record.totalHoursWorked),
        snap?.horasNormales ?? safeToFixed(record.authorizedHours),
        snap?.turnosDobles ?? record.doubleShifts ?? 0,
        snap?.horasExtras ??
          safeToFixed(record.extraHours),
        record.freeDaysWorked ?? 0,
        record.freeDaysEnjoyed ?? 0,
        record.celebrityDaysWorked || 0,
        record.totalIncentives || "$0.00",
        deductions.totalDeductions,
        deductions.tarjetas,
        snap?.incidentsTotal ?? record.totalIncidents ?? 0,
        "—",
        snap?.pagoEstimado ??
          record.totalPayWithIncentives ??
          "$0.00",
      ];
      data.push(row);
    });

    data.push([]);
    let sumDays = 0;
    let sumJornadas = 0;
    let sumHorasTrab = 0;
    let sumHorasNorm = 0;
    let sumDobles = 0;
    let sumExtras = 0;
    let sumFreeW = 0;
    let sumFreeE = 0;
    let sumCel = 0;
    let sumBonos = 0;
    let sumTarjetas = 0;
    let sumDed = 0;
    let sumInc = 0;
    let sumPago = 0;

    rowsForExport.forEach((record: any) => {
      const snap = cuadroExportSnapshots.get(record.partnerId);
      const deductions = getCuadroDeductionColumnsForExport(record);
      const d = snap?.daysWorked ?? record.daysWorked ?? 0;
      sumDays += typeof d === "number" ? d : parseFloat(String(d)) || 0;
      sumJornadas += snap?.jornadasCompletadas ?? record.jornadasCompletas ?? 0;
      sumHorasTrab +=
        parseFloat(String(snap?.horasTrabajadas ?? safeToFixed(record.totalHoursWorked))) ||
        0;
      sumHorasNorm +=
        parseFloat(String(snap?.horasNormales ?? safeToFixed(record.authorizedHours))) ||
        0;
      sumDobles += snap?.turnosDobles ?? record.doubleShifts ?? 0;
      sumExtras += extraHoursNumeric(snap, record);
      sumFreeW += record.freeDaysWorked || 0;
      sumFreeE += record.freeDaysEnjoyed || 0;
      sumCel += record.celebrityDaysWorked || 0;
      sumBonos += parseMoney(record.totalIncentives);
      sumTarjetas += parseMoney(deductions.tarjetas);
      sumDed += parseMoney(deductions.totalDeductions);
      sumInc += snap?.incidentsTotal ?? record.totalIncidents ?? 0;
      sumPago += parseMoney(
        snap?.pagoEstimado ?? record.totalPayWithIncentives
      );
    });

    const totalsRow = [
      "TOTALES",
      "",
      "",
      "",
      sumDays,
      sumJornadas,
      safeToFixed(sumHorasTrab),
      safeToFixed(sumHorasNorm),
      sumDobles,
      sumExtras,
      sumFreeW,
      sumFreeE,
      sumCel,
      getCurrencyFormat(sumBonos),
      getCurrencyFormat(sumDed),
      getCurrencyFormat(sumTarjetas),
      sumInc,
      "",
      getCurrencyFormat(sumPago),
    ];
    data.push(totalsRow);

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    const titleStyle = {
      font: { bold: true, sz: 18, color: { rgb: "1F4E79" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    const infoStyle = {
      font: { bold: true, sz: 11, color: { rgb: "374151" } },
      alignment: { horizontal: "left", vertical: "center" },
    };

    const infoValueStyle = {
      font: { sz: 11, color: { rgb: "1F4E79" } },
      alignment: { horizontal: "left", vertical: "center" },
    };

    const headerStyle = {
      font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "50B7C0" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "FFFFFF" } },
        bottom: { style: "thin", color: { rgb: "FFFFFF" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };

    const dataStyle = {
      font: { sz: 10, color: { rgb: "374151" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "thin", color: { rgb: "E5E7EB" } },
      },
    };

    const dataStyleAlt = {
      ...dataStyle,
      fill: { fgColor: { rgb: "F9FAFB" } },
    };

    const nameStyle = {
      ...dataStyle,
      alignment: { horizontal: "left", vertical: "center" },
      font: { sz: 10, color: { rgb: "1F4E79" }, bold: true },
    };

    const nameStyleAlt = {
      ...nameStyle,
      fill: { fgColor: { rgb: "F9FAFB" } },
    };

    const totalsStyle = {
      font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1F4E79" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "medium", color: { rgb: "1F4E79" } },
        bottom: { style: "medium", color: { rgb: "1F4E79" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };

    const totalsTitleStyle = {
      ...totalsStyle,
      alignment: { horizontal: "left", vertical: "center" },
    };

    if (worksheet["A1"]) worksheet["A1"].s = titleStyle;

    if (worksheet["A3"]) worksheet["A3"].s = infoStyle;
    if (worksheet["B3"]) worksheet["B3"].s = infoValueStyle;
    if (worksheet["D3"]) worksheet["D3"].s = infoStyle;
    if (worksheet["E3"]) worksheet["E3"].s = infoValueStyle;
    if (worksheet["A4"]) worksheet["A4"].s = infoStyle;
    if (worksheet["B4"]) worksheet["B4"].s = infoValueStyle;

    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: 5, c: i });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = headerStyle;
      }
    }

    const dataStartRow = 6;
    const dataEndRow = dataStartRow + rowsForExport.length;
    for (let row = dataStartRow; row < dataEndRow; row++) {
      const isAlt = (row - dataStartRow) % 2 === 1;
      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellRef]) {
          if (col === 0) {
            worksheet[cellRef].s = isAlt ? nameStyleAlt : nameStyle;
          } else {
            worksheet[cellRef].s = isAlt ? dataStyleAlt : dataStyle;
          }
        }
      }
    }

    const totalsRowIndex = dataEndRow + 1;
    for (let col = 0; col < headers.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: totalsRowIndex, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = col === 0 ? totalsTitleStyle : totalsStyle;
      }
    }

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    ];

    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 13 },
      { wch: 13 },
      { wch: 12 },
      { wch: 15 },
      { wch: 14 },
      { wch: 13 },
      { wch: 12 },
      { wch: 11 },
      { wch: 14 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 11 },
      { wch: 14 },
    ];

    worksheet["!rows"] = [
      { hpt: 30 },
      { hpt: 15 },
      { hpt: 20 },
      { hpt: 20 },
      { hpt: 15 },
      { hpt: 25 },
      { hpt: 35 },
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "Cuadro de Mando");

    const fileName = `Cuadro_Mando_${branchName.replace(
      /\s+/g,
      "_"
    )}_${startDate}_${endDate}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    notification("Éxito", "Excel descargado correctamente", "success");
  };

  React.useEffect(() => {
    if (selectedBranch.length === 0) {
      setBranchSelected(null);
      return;
    }

    const exist = selectedBranch.some(
      (selected) => selected?.id === branchSelected?.id
    );

    if (!exist) {
      setBranchSelected(selectedBranch[0]);
    }
  }, [selectedBranch, branchSelected]);

  // Limpiar la función de exportación cuando cambia el modo de vista
  React.useEffect(() => {
    if (viewMode === "cuadro") {
      setExportResumenExcel(null);
    }
  }, [viewMode]);

  // Callback para actualizar los valores calculados de cada colaborador
  const handleCalculationUpdate = React.useCallback(
    (partnerId: number, calculation: any) => {
      setPartnerCalculations((prev) => {
        const newMap = new Map(prev);
        newMap.set(partnerId, calculation);
        return newMap;
      });
    },
    []
  );

  // Método que calcula totalPayWithIncentives total sumando los valores calculados individualmente
  // de cada colaborador (igual que se muestra en el desglose)
  const calculateTotalPayWithIncentives = React.useMemo(() => {
    if (records.length === 0 || !branchSelected) {
      return {
        totalDailyPayments: 0,
        totalIncentives: 0,
        totalDeductions: 0,
        totalMissingBoxDeductions: 0,
        uniformDeduction: 0,
        totalPayWithIncentives: 0,
        totalPayWithIncentivesFormatted: getCurrencyFormat(0),
      };
    }

    let totalDailyPaymentsSum = 0;
    let totalIncentivesSum = 0;
    let totalDeductionsSum = 0;
    let totalMissingBoxDeductionsSum = 0;
    let uniformDeductionSum = 0;
    let totalPayWithIncentivesSum = 0;

    // Sumar los valores calculados de cada colaborador (los mismos que se muestran en el desglose)
    records.forEach((record: any) => {
      const partnerCalc = partnerCalculations.get(record.partnerId);
      
      if (partnerCalc && partnerCalc.totalPayWithIncentives > 0) {
        // Usar los valores calculados por el hook (igual que PaymentDetail.tsx)
        // Estos son los mismos valores que se muestran en el desglose por colaborador
        totalDailyPaymentsSum += partnerCalc.totalDailyPayments || 0;
        totalIncentivesSum += partnerCalc.totalIncentives || 0;
        totalDeductionsSum += partnerCalc.totalDeductions || 0;
        totalMissingBoxDeductionsSum += partnerCalc.totalMissingBoxDeductions || 0;
        uniformDeductionSum += partnerCalc.uniformDeduction || 0;
        totalPayWithIncentivesSum += partnerCalc.totalPayWithIncentives || 0;
      } else if (partnerCalc) {
        // Si el cálculo existe pero el total es 0, aún así usarlo (puede ser un colaborador sin pagos)
        totalDailyPaymentsSum += partnerCalc.totalDailyPayments || 0;
        totalIncentivesSum += partnerCalc.totalIncentives || 0;
        totalDeductionsSum += partnerCalc.totalDeductions || 0;
        totalMissingBoxDeductionsSum += partnerCalc.totalMissingBoxDeductions || 0;
        uniformDeductionSum += partnerCalc.uniformDeduction || 0;
        totalPayWithIncentivesSum += partnerCalc.totalPayWithIncentives || 0;
      } else {
        // Si aún no se han calculado, usar valores del record como fallback temporal
        // Esto solo ocurre mientras se están cargando los datos
        const totalIncentives = Number(
          (record.totalIncentives || "$0.00").replace(/[^0-9.-]+/g, "") || 0
        );
        totalIncentivesSum += totalIncentives;

        const tarjetas = Number(
          (record.tarjetasDeductions || "$0.00").replace(/[^0-9.-]+/g, "") || 0
        );
        const excluding = Number(
          (
            record.totalDeductionsExcludingTarjetas ??
            record.totalDeductions ??
            "$0.00"
          )
            .toString()
            .replace(/[^0-9.-]+/g, "") || 0
        );
        totalDeductionsSum +=
          tarjetas > 0 || record.totalDeductionsExcludingTarjetas != null
            ? excluding + tarjetas
            : excluding;

        const missingBoxDeductions = (record.calculatedMissingBoxes || []).reduce(
          (sum: number, missingBox: any) => sum + (missingBox.calculatedAmount || 0),
          0
        );
        totalMissingBoxDeductionsSum += missingBoxDeductions;

        const uniformDeduction = Number(
          (record.uniformDeduction || "0").replace(/[^0-9.-]+/g, "") || 0
        );
        uniformDeductionSum += uniformDeduction;

        // Para totalPayWithIncentives, usar el valor del record como aproximación temporal
        const recordTotalPay = Number(
          (record.totalPayWithIncentives || "$0.00").replace(/[^0-9.-]+/g, "") || 0
        );
        totalPayWithIncentivesSum += recordTotalPay;
      }
    });

    return {
      totalDailyPayments: totalDailyPaymentsSum,
      totalIncentives: totalIncentivesSum,
      totalDeductions: totalDeductionsSum,
      totalMissingBoxDeductions: totalMissingBoxDeductionsSum,
      uniformDeduction: uniformDeductionSum,
      totalPayWithIncentives: totalPayWithIncentivesSum,
      totalPayWithIncentivesFormatted: getCurrencyFormat(
        totalPayWithIncentivesSum
      ),
    };
  }, [records, branchSelected, partnerCalculations]);

  return (
    <React.Fragment>
      {/* Sucursales seleccionadas */}
      {selectedBranch.length > 0 && (
        <div
          className="mb-3"
          style={{
            background: "#ffffff",
            borderRadius: "20px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              background: "#f8fafc",
              padding: "0.6rem 1rem",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
              <span
                style={{
                  color: "#64748b",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                Sucursales seleccionadas
              </span>
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                <button
                  type="button"
                  className="border-0"
                  onClick={() => setViewMode("cuadro")}
                  style={{
                    background:
                      viewMode === "cuadro"
                        ? "linear-gradient(135deg, #1F4E79 0%, #2F5597 100%)"
                        : "#e2e8f0",
                    color: viewMode === "cuadro" ? "#ffffff" : "#475569",
                    borderRadius: "12px",
                    padding: "0.4rem 0.85rem",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                  }}
                >
                  Cuadro de Mando
                </button>
                 <button
                  type="button"
                  className="border-0"
                  onClick={() => setViewMode("resumen")}
                  style={{
                    background:
                      viewMode === "resumen"
                        ? "linear-gradient(135deg, #1F4E79 0%, #2F5597 100%)"
                        : "#e2e8f0",
                    color: viewMode === "resumen" ? "#ffffff" : "#475569",
                    borderRadius: "12px",
                    padding: "0.4rem 0.85rem",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                  }}
                >
                  Pago Estimado Total
                </button> 
              </div>
            </div>
            {(viewMode === "cuadro" && branchSelected) || viewMode === "resumen" ? (
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                {viewMode === "cuadro" && branchSelected && (
                  <>
                    <button
                      type="button"
                      className="border-0 d-flex align-items-center"
                      onClick={exportTableToExcel}
                      title="Descargar tabla cuadro de mando en Excel"
                      disabled={!canExportCuadroTabla}
                      style={{
                        background: !canExportCuadroTabla
                            ? "#d1d5db"
                            : "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)",
                        color: "#ffffff",
                        borderRadius: "12px",
                        padding: "0.35rem 0.75rem",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        transition: "all 0.2s ease",
                        cursor: !canExportCuadroTabla ? "not-allowed" : "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (canExportCuadroTabla) {
                          e.currentTarget.style.opacity = "0.9";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (canExportCuadroTabla) {
                          e.currentTarget.style.opacity = "1";
                        }
                      }}
                    >
                      <i
                        className="fas fa-download me-1"
                        style={{ fontSize: "0.7rem" }}
                      ></i>
                      Tabla cuadro mando
                    </button>
                    <button
                      type="button"
                      className="border-0 d-flex align-items-center"
                      onClick={() => extendedExportApi?.export()}
                      title="Descargar cuadro finanzas en Excel"
                      disabled={!extendedExportApi?.canExport}
                      style={{
                        background: !extendedExportApi?.canExport
                          ? "#d1d5db"
                          : "linear-gradient(135deg, #1F4E79 0%, #2F5597 100%)",
                        color: "#ffffff",
                        borderRadius: "12px",
                        padding: "0.35rem 0.75rem",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        transition: "all 0.2s ease",
                        cursor: !extendedExportApi?.canExport
                          ? "not-allowed"
                          : "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (extendedExportApi?.canExport) {
                          e.currentTarget.style.opacity = "0.9";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (extendedExportApi?.canExport) {
                          e.currentTarget.style.opacity = "1";
                        }
                      }}
                    >
                      <i
                        className="fas fa-download me-1"
                        style={{ fontSize: "0.7rem" }}
                      ></i>
                      Cuadro finanzas
                    </button>
                    <button
                      type="button"
                      className="border-0 d-flex align-items-center"
                      onClick={() => setShowBulkReciboModal(true)}
                      title="Descargar recibos masivos"
                      style={{
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "#ffffff",
                        borderRadius: "12px",
                        padding: "0.35rem 0.75rem",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "0.9";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                    >
                      <i
                        className="fas fa-download me-1"
                        style={{ fontSize: "0.7rem" }}
                      ></i>
                      Recibos
                    </button>
                  </>
                )}
                {viewMode === "resumen" && (
                  <button
                    type="button"
                    className="border-0 d-flex align-items-center"
                    onClick={() => {
                      if (exportResumenExcel) {
                        exportResumenExcel();
                      }
                    }}
                    title="Descargar tabla en Excel"
                    disabled={exportResumenExcel === null}
                    style={{
                      background: exportResumenExcel !== null
                        ? "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)"
                        : "#d1d5db",
                      color: "#ffffff",
                      borderRadius: "12px",
                      padding: "0.35rem 0.75rem",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                      cursor: exportResumenExcel !== null ? "pointer" : "not-allowed",
                    }}
                    onMouseEnter={(e) => {
                      if (exportResumenExcel !== null) {
                        e.currentTarget.style.opacity = "0.9";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (exportResumenExcel !== null) {
                        e.currentTarget.style.opacity = "1";
                      }
                    }}
                  >
                    <i
                      className="fas fa-download me-1"
                      style={{ fontSize: "0.7rem" }}
                    ></i>
                    Excel
                  </button>
                )}
              </div>
            ) : null}
          </div>
          <div style={{ padding: "0.75rem 1rem" }}>
            <div
              className="d-flex flex-wrap align-items-center"
              style={{ gap: "0.5rem" }}
            >
              {selectedBranch.map((branch) => {
                const isActive =
                  branchSelected && branchSelected.id === branch.id;
                return (
                  <button
                    key={branch.id}
                    className="border-0"
                    style={{
                      background: isActive
                        ? "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)"
                        : "#f1f5f9",
                      color: isActive ? "#ffffff" : "#475569",
                      borderRadius: "14px",
                      padding: "0.4rem 0.85rem",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                      boxShadow: isActive
                        ? "0 2px 6px rgba(80, 183, 192, 0.35)"
                        : "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background = "#e2e8f0";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background = "#f1f5f9";
                      }
                    }}
                    onClick={() => {
                      setBranchSelected(branch);
                    }}
                  >
                    {branch.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Vista: Pago Estimado Total (Resumen General) */}
      {viewMode === "resumen" && (
        <PaymentEstimadoTotal
          selectedBranch={selectedBranch}
          dates={dates}
          onExportExcel={(exportFn) => {
            setExportResumenExcel(() => exportFn);
          }}
        />
      )}

      {/* Vista: Cuadro de Mando */}
      {viewMode === "cuadro" && (
        <>

          {/* Tabla  */}
          {!branchSelected && (
            <div className="d-flex align-items-center justify-content-center">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <EmptyImage className="mx-auto w-250px" />
                <h5 className="text-secondary text-center fs-20px mt-3">
                  Sin sucursal seleccionada
                </h5>
              </div>
            </div>
          )}

          {branchSelected && (
            <PayrollBranch
              branch={Number(branchSelected.id)}
              branchName={branchSelected.name}
              dates={dates}
              specialPartners={specialPartners}
              onAddSpecialPartner={handleAddSpecialPartner}
              onRemoveSpecialPartner={handleRemoveSpecialPartner}
              onCuadroExportSnapshot={handleCuadroExportSnapshot}
              onRegisterExtendedExport={setExtendedExportApi}
            />
          )}
        </>
      )}

      <BulkRecibosModal
        open={showBulkReciboModal}
        onClose={() => setShowBulkReciboModal(false)}
        branchSelected={branchSelected}
        dates={dates}
        specialPartners={specialPartners}
      />
    </React.Fragment>
  );
};

export default CuadroMando;
